import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  Component,
  computed,
  inject,
  input,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAddressComponent,
  OnyxLanguagePipe,
  OnyxPhonePipe,
} from '@onyx/angular';
import { chain, isEqual, uniqBy } from 'lodash';
import type { Observable } from 'rxjs';
import { first, forkJoin, map, of } from 'rxjs';
import { validate as validateUuid } from 'uuid';
import { BUSINESS_HOURS_KEYS } from '../../../../../../../../../common/constants/common/business-hours-keys';
import { DAY_TIME_RANGE } from '../../../../../../../../../common/constants/common/day-time-range';
import { DictionaryCode } from '../../../../../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../../../../../common/helpers/route.helper';
import type { BusinessHours } from '../../../../../../../../../common/interfaces/common/business-hours';
import { GoodQuantityPipe } from '../../../../../../../../../common/pipes/good-quantity.pipe';
import { CompanyDictionaryCode } from '../../../../../../../../management-panel/dictionaries/common/enums/company-dictionary-code';
import { CompanyDictionariesService } from '../../../../../../../../management-panel/dictionaries/common/services/company-dictionaries.service';
import { OrderPointCategory } from '../../../../../../../common/enums/order-point-category';
import { OrderHelper } from '../../../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../../../common/interfaces/order';
import type {
  OrderLoadingGood,
  OrderLoadingPoint,
  OrderPoint,
  OrderUnloadingPoint,
} from '../../../../../../../common/interfaces/order-point';

@Component({
  selector: 'app-order-pdf-template-route',
  imports: [
    TranslatePipe,
    UpperCasePipe,
    OnyxAddressComponent,
    OnyxLanguagePipe,
    OnyxPhonePipe,
    AsyncPipe,
    GoodQuantityPipe,
    DecimalPipe,
  ],
  templateUrl: './order-pdf-template-route.component.html',
  styleUrl: './order-pdf-template-route.component.scss',
})
export class OrderPdfTemplateRouteComponent {
  private readonly orderHelper = inject(OrderHelper);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.pdfTemplate';
  protected readonly BUSINESS_HOURS_KEYS = BUSINESS_HOURS_KEYS;

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrderPointCategory = OrderPointCategory;

  protected orderPoints = computed(() =>
    this.order().points.filter((point) => RouteHelper.isIncludedInOrder(point)),
  );
  protected loadingPoints = computed<OrderLoadingPoint[]>(() =>
    this.order().points.filter(
      (point) => point.category === OrderPointCategory.LOADING,
    ),
  );
  protected unloadingPoints = computed<OrderUnloadingPoint[]>(() =>
    this.order().points.filter(
      (point) => point.category === OrderPointCategory.UNLOADING,
    ),
  );

  protected lastPointItemHeight = computed(() => {
    const items = this.routeItems();
    if (!items.length) return 0;

    const lastItem = items.at(-1);
    return lastItem?.nativeElement?.clientHeight || 0;
  });

  private routeItems = viewChildren<ElementRef<HTMLElement>>('routeItem');

  protected businessHoursAlwaysOpen(businessHours: BusinessHours): boolean {
    return Object.values(businessHours).every((openingHours) =>
      isEqual(openingHours, DAY_TIME_RANGE),
    );
  }

  protected goods$(point: OrderPoint): Observable<OrderLoadingGood[] | null> {
    if (!('goods' in point)) return of(null);

    let formGoods: OrderLoadingGood[];
    if (point.category === OrderPointCategory.LOADING) {
      formGoods = point.goods;
    } else if (point.category === OrderPointCategory.UNLOADING) {
      formGoods = chain(point.goods)
        .map(({ uuid, quantity }) => {
          const good = this.loadingPointGoods().find(
            (good) => good.uuid === uuid,
          );
          if (!good) return null;

          return { ...good, quantity };
        })
        .compact()
        .value();
    }

    return forkJoin([
      this.companyDictionariesService
        .listDictionary(CompanyDictionaryCode.GOOD_TYPE)
        .pipe(first()),
      this.companyDictionariesService
        .listDictionary(CompanyDictionaryCode.UNIT)
        .pipe(first()),
    ]).pipe(
      map(([goodTypes, units]) =>
        formGoods.map(
          (good) =>
            ({
              ...good,
              type: validateUuid(good.type?.uuid ?? '')
                ? (goodTypes.find(({ uuid }) => uuid === good.type?.uuid) ??
                  null)
                : null,
              typeName: !validateUuid(good.type?.uuid ?? '') ? good.type : null,
              unit: units.find(({ uuid }) => uuid === good.unit.uuid),
            }) as OrderLoadingGood,
        ),
      ),
    );
  }

  protected loadingPointGoods(): OrderLoadingGood[] {
    return this.order()
      .points.filter((point) => point.category === OrderPointCategory.LOADING)
      .map((point) => point.goods)
      .flatMap((goods) => uniqBy(goods, 'uuid'));
  }

  protected unloadingPointGoods(point: OrderPoint): OrderLoadingGood[] {
    if (point.category !== OrderPointCategory.UNLOADING) return [];

    const unloadingGoods = point.goods;
    const loadingsGoods = this.loadingPoints()?.flatMap((point) => point.goods);
    if (!unloadingGoods) return [];

    return loadingsGoods?.map((loadingGood) => {
      const unloadingGood = unloadingGoods.find(
        (good) => good.uuid === loadingGood.uuid,
      );

      return {
        ...loadingGood,
        quantity: unloadingGood
          ? unloadingGood.quantity
          : OrderHelper.getGoodQuantity(loadingGood),
      };
    });
  }

  protected getTimeWindows(point: OrderPoint): {
    label: string;
    value: string[];
  } {
    return this.orderHelper.getTimeWindows(point);
  }
}
